import { type SetStateAction, useCallback, useRef, useState } from 'react';
import type { Message } from '../../chat-types';

export const useMessagesState = (initializer: () => Message[], onMessagesChange?: (messages: Message[]) => void) => {
  const [messages, setMessagesState] = useState<Message[]>(initializer);
  const messagesRef = useRef(messages);
  const onChangeRef = useRef(onMessagesChange);
  const initialPublishPendingRef = useRef(true);

  messagesRef.current = messages;

  if (onChangeRef.current !== onMessagesChange) {
    onChangeRef.current = onMessagesChange;
    initialPublishPendingRef.current = true;
  }

  if (initialPublishPendingRef.current && typeof onChangeRef.current === 'function') {
    initialPublishPendingRef.current = false;
    queueMicrotask(() => {
      onChangeRef.current?.(messagesRef.current);
    });
  }

  const setMessages = useCallback((updater: SetStateAction<Message[]>) => {
    setMessagesState((previous) => {
      const next = typeof updater === 'function' ? (updater as (prev: Message[]) => Message[])(previous) : updater;
      messagesRef.current = next;
      onChangeRef.current?.(next);
      return next;
    });
  }, []);

  return [messages, setMessages] as const;
};
